import { useState } from 'react';
import { Button } from './ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from './ui/popover';
import { Filter, X, Star } from 'lucide-react';
import { cn } from '../lib/utils';

export interface WordFilters {
  confidenceLevels: number[];
  sortBy: 'newest' | 'oldest' | 'alphabetical';
}

interface WordFilterPopoverProps {
  filters: WordFilters;
  onFiltersChange: (filters: WordFilters) => void;
}

export function WordFilterPopover({ filters, onFiltersChange }: WordFilterPopoverProps) {
  const [filterOpen, setFilterOpen] = useState(false);
  const [tempConfidenceFilter, setTempConfidenceFilter] = useState<number[]>(filters.confidenceLevels);
  const [tempSortBy, setTempSortBy] = useState<'newest' | 'oldest' | 'alphabetical'>(filters.sortBy);

  const openFilterPopover = () => {
    setTempConfidenceFilter(filters.confidenceLevels); // Preserve selected confidence levels
    setTempSortBy(filters.sortBy);
    setFilterOpen(true);
  };

  const toggleTempConfidence = (level: number) => {
    setTempConfidenceFilter(prev =>
      prev.includes(level)
        ? prev.filter(l => l !== level)
        : [...prev, level]
    );
  };

  const applyFilters = () => {
    onFiltersChange({
      confidenceLevels: [...tempConfidenceFilter],
      sortBy: tempSortBy
    });
    setFilterOpen(false);
  };

  const resetFilters = () => {
    setTempConfidenceFilter([]);
    setTempSortBy('newest');
  };

  const hasActiveFilters = (filters.confidenceLevels.length > 0 && filters.confidenceLevels.length < 5) || filters.sortBy !== 'newest';

  return (
    <Popover open={filterOpen} onOpenChange={setFilterOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className="gap-2 bg-transparent"
          onClick={openFilterPopover}
        >
          <Filter className="h-4 w-4" />
          Filter
          {hasActiveFilters && (
            <span className="flex h-2 w-2 rounded-full bg-primary" />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-88 p-0">
        {/* Header */}
        <div className="border-b p-4">
          <div className="flex items-center justify-between">
            <div>
              <h4 className="font-semibold text-foreground">Filter Words</h4>
            </div>
            <button
              onClick={() => setFilterOpen(false)}
              className="rounded-full p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-4">
          {/* Confidence Level */}
          <div>
            <label className="text-sm font-medium text-foreground">Confidence Level</label>
            <div className="mt-2 flex gap-2">
              {[1, 2, 3, 4, 5].map((level) => {
                const isSelected = tempConfidenceFilter.includes(level)
                return (
                  <button
                    key={level}
                    onClick={() => toggleTempConfidence(level)}
                    className={cn(
                      "flex items-center gap-1 rounded-lg border px-3 py-2 text-sm font-medium transition-all",
                      isSelected
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    )}
                  >
                    {level}
                    <Star className={cn(
                      "h-3.5 w-3.5",
                      isSelected ? "fill-primary text-primary" : "fill-muted-foreground/50 text-muted-foreground/50"
                    )} />
                  </button>
                )
              })}
            </div>
          </div>

          {/* Sort By */}
          <div>
            <label className="text-sm font-medium text-foreground flex items-center gap-2">
              <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M7 15l5 5 5-5M7 9l5-5 5 5" />
              </svg>
              Sort By
            </label>
            <div className="mt-2 space-y-1">
              {[
                { value: "newest" as const, label: "Newest First" },
                { value: "oldest" as const, label: "Oldest First" },
                { value: "alphabetical" as const, label: "Alphabetical" },
              ].map((option) => (
                <button
                  key={option.value}
                  onClick={() => setTempSortBy(option.value)}
                  className={cn(
                    "w-full rounded-lg px-3 py-2.5 text-left text-sm font-medium transition-all",
                    tempSortBy === option.value
                      ? "bg-foreground text-background"
                      : "bg-muted/50 text-muted-foreground hover:bg-muted hover:text-foreground"
                  )}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between border-t p-4">
          <button
            onClick={resetFilters}
            className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
            Reset
          </button>
          <Button size="sm" onClick={applyFilters}>
            Apply Filters
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}